import { useEffect } from 'react';

import { useRegion } from '@/context';

/** In-app banner cadence: the user is working, not waiting on this screen. */
export const BANNER_CLAIMS_POLL_MS = 60_000;

/** Device-use dialog cadence: the user is blocked on this screen, waiting on their inbox. */
export const DIALOG_CLAIMS_POLL_MS = 10_000;

/**
 * Keep asking the identity provider for a fresh token while something in the
 * app waits on the verified-email claim (ESCLOUD-286).
 *
 * The access token minted at sign-in cannot know about a verification that
 * happens afterwards, so the caller polls a forced token refresh: the Auth0
 * post-login action re-runs on the exchange and recomputes the claim. A
 * failed refresh is what turns a wait into "this cannot be established", so
 * whatever reports that has to keep trying too, or a passing network blip
 * would strand a user who was only ever waiting for their inbox.
 *
 * Two callers: the in-app banner (`BANNER_CLAIMS_POLL_MS`, the default) and
 * the device-use dialog (`DIALOG_CLAIMS_POLL_MS`), which blocks the clinical
 * route and so asks more often.
 */
export function useClaimsRefreshPoll(intervalMs: number = BANNER_CLAIMS_POLL_MS): void {
    const { refreshToken } = useRegion();

    useEffect(() => {
        // A failed poll is already logged by refreshToken(); the next tick or a
        // tab focus retries, so there is nothing to surface per attempt.
        const tick = () => void refreshToken().catch(() => undefined);

        // Immediately too: the user may have clicked the link before this
        // screen mounted (or in another tab — hence the visibility listener).
        tick();
        const interval = setInterval(tick, intervalMs);
        const onVisible = () => {
            if (document.visibilityState === 'visible') tick();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(interval);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refreshToken, intervalMs]);
}
